import { Button, Dropdown, Space, Typography, notification } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Enums } from '../../../shared';
import { CourseDraftStepType } from '../../../types/course';
import {
    useGetComponentListQuery,
    useIssueCommandMutation,
} from '../../../features/courses/coursesApi';
import { useNavigate } from 'react-router-dom';
import {
    FileDoneOutlined,
    ReadOutlined,
    DownOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

type CourseActionButtonDataType = {
    id: string;
    steps: Array<CourseDraftStepType>;
    description: string;
    editable: boolean;
};

const mapToCourseSteps = (data) => {
    return data.map((item: CourseDraftStepType, index: number) => ({
        component: item.component,
        order: index + 1,
        evaluation_type: item.evaluation_type,
    }));
};
const validateBeforeSave = (steps, description) => {
    return steps.length > 0 || description.length > 0;
};

const validateBeforePublish = (steps, description) => {
    return steps.length > 0 && description.length > 0;
};

const PublishButton = ({ course }: { course: CourseActionButtonDataType }) => {
    const { t } = useTranslation();
    const [issueCommand, {}] = useIssueCommandMutation();
    const navigate = useNavigate();

    const publish = () => {
        const command = {
            type: Enums.COMMAND_TYPES.PUBLISH_COURSE,
            courseId: course.id,
            description: course.description,
            steps: mapToCourseSteps(course.steps),
        };

        if (!validateBeforePublish(course.steps, course.description)) {
            notification.error({
                message: t(
                    'You need to add at least one step and a description'
                ),
            });
            return;
        }

        issueCommand({ command })
            .unwrap()
            .then((res) => {
                notification.info({
                    message: t('CoursePublished'),
                    duration: 2,
                });
                navigate('/app/courses/');
            })
            .catch((err) => {
                notification.error({
                    message: t('Error publishing course'),
                });
            });
    };

    return (
        <Button
            data-cy="course-details-edit-publish"
            onClick={publish}
            type="primary"
        >
            {t('publish')}
        </Button>
    );
};

const SaveButton = ({ course }: { course: CourseActionButtonDataType }) => {
    const { t } = useTranslation();
    const [issueCommand, {}] = useIssueCommandMutation();
    const save = () => {
        const command = {
            type: Enums.COMMAND_TYPES.UPDATE_COURSE,
            description: course.description,
            steps: mapToCourseSteps(course.steps),
            courseId: course.id,
        };

        if (!validateBeforeSave(command.steps, command.description)) {
            notification.error({
                message: t('Please add at least one step or description'),
            });
            return;
        }

        issueCommand({ command })
            .unwrap()
            .then((res) => {
                notification.info({
                    message: t('CourseSaved'),
                    duration: 2,
                });
            })
            .catch((err) => {
                notification.error({
                    message: t('Error saving course'),
                });
            });
    };
    return (
        <Button
            data-cy="course-details-edit-save"
            onClick={save}
            type="primary"
        >
            {t('save')}
        </Button>
    );
};

const getActions = (
    courseData: CourseActionButtonDataType
): Array<React.ReactNode> => {
    if (courseData.editable) {
        return [
            <PublishButton course={courseData} key={'publish'} />,
            <SaveButton course={courseData} key={'save'} />,
        ];
    } else {
        return [];
    }
};

const AddComponentButton = ({ steps, onStepsUpdate }) => {
    const { t } = useTranslation();
    const [count, setCount] = useState(steps.length);

    const { data: availableSteps } = useGetComponentListQuery(
        'course-components-catalog'
    );

    const handleAdd = (clickEvent) => {
        const chosenElement: CourseDraftStepType = availableSteps.find(
            (availableSteps) => availableSteps.id == clickEvent.key
        );

        const newData: CourseDraftStepType = {
            id: chosenElement.id,
            evaluation_type: Enums.COURSE_STEP_EVALUATION_TYPE.SELF_EVALUATED,
            component: chosenElement.id,
            title: chosenElement.title,
            type: chosenElement.type,
            description: chosenElement.description,
            order: steps.length + 1,
        };

        onStepsUpdate([...steps, newData]);
        setCount(count + 1);
    };

    const isAvailable = (item: CourseDraftStepType) => {
        return steps.find(
            (element: CourseDraftStepType) => element.component === item.id
        );
    };

    const mapToDropdown = (items: Array<CourseDraftStepType>) => {
        return items?.map((item) => ({
            label: (
                <Space direction="horizontal">
                    <Text
                        strong
                        type={isAvailable(item) ? 'secondary' : undefined}
                    >
                        {item.title}
                    </Text>
                    <Text type="secondary">{item.description}</Text>
                </Space>
            ),
            key: item.id,
            disabled: isAvailable(item),
        }));
    };
    return (
        <Dropdown
            menu={{
                items: mapToDropdown(availableSteps),
                onClick: handleAdd,
            }}
        >
            <Button block type="primary">
                <Space>
                    {t('workbench.courses.addStep')}
                    <DownOutlined />
                </Space>
            </Button>
        </Dropdown>
    );
};

export default getActions;

export { AddComponentButton };
